import React from 'react';
import styled from 'styled-components';
import LinkButton from '@components/shared/LinkButton';
import { PageSlice, SliceGridDetails, SliceGridItem } from '@contracts';
import Colors from '../Colors';
import LazyLoadImage from '../LazyLoadImage';
import Text from '../Text';
import useDesktop from '../useDesktop';
import GridVariant2 from './GridVariant2';
import { TEXT_STYLES, TextVariant } from './Typography';

interface GridProps {
  slice: PageSlice;
}

const Grid: React.FC<GridProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceGridDetails;
  const sliceItems = slice.items as SliceGridItem[];
  const desktop = useDesktop(450);

  const getWrapper = (variant: number) => {
    switch (variant) {
      case 2:
        return <GridVariant2 slice={slice} />;
      default:
        return (
          <Wrapper>
            {sliceDetails?.overline && <Overline>{sliceDetails.overline}</Overline>}
            {sliceDetails?.heading?.text && <Heading>{sliceDetails.heading.text}</Heading>}
            {sliceDetails?.subheading?.text && <SubHeading>{sliceDetails.subheading.text}</SubHeading>}
            <GridWrap>
              {sliceItems.map((item, index) => (
                <GridItem key={index}>
                  {item?.image?.url ? <LazyLoadImage src={item.image.url} alt={item.image.alt || ''} /> : null}
                  <GridItemBody>
                    {item?.heading?.text && (
                      <h4>
                        <Text richText={desktop ? item.heading.richText : item.mobile_heading.richText} />
                      </h4>
                    )}
                    {item?.bodycopy?.text && <p>{desktop ? item.bodycopy.text : item.mobile_bodycopy.text}</p>}
                    {item?.supbodycopy?.text && <span>{item.supbodycopy.text}</span>}
                  </GridItemBody>
                </GridItem>
              ))}
            </GridWrap>
            {sliceDetails?.buttonlink?.url && sliceDetails?.buttontext?.text && (
              <CTA to={sliceDetails.buttonlink.url}>{sliceDetails.buttontext.text}</CTA>
            )}
          </Wrapper>
        );
    }
  };

  return <GridContainer $background={sliceDetails?.background_text}>{getWrapper(sliceDetails?.variant)}</GridContainer>;
};

export default Grid;

const GridContainer = styled.section<{ $background?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  background: ${(props) => props.$background || 'transparent'};
  color: ${Colors.navyBlue};

  span.pink {
    color: ${Colors.lilac};
  }
`;

const Wrapper = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  padding: 96px 0;
  text-align: center;

  @media (max-width: 450px) {
    padding: 56px 0;
  }
`;

const Overline = styled.div`
  ${TEXT_STYLES[TextVariant.UpperText4]};
  text-align: center;
  margin: 0 auto 16px;
`;

const Heading = styled.h2`
  ${TEXT_STYLES[TextVariant.Heading4]};
  text-align: center;
  margin: 0 auto 16px;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const SubHeading = styled.h2`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: center;
  max-width: 640px;
  margin: 0 auto;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;

const GridWrap = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  column-gap: 16px;
  margin-top: 32px;

  @media (max-width: 800px) {
    flex-direction: column;
    row-gap: 32px;
    align-items: stretch;
  }

  @media (max-width: 450px) {
    row-gap: 16px;
    margin-top: 24px;
  }
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  max-width: 400px;
  margin: 0;
  flex: 1;

  @media (max-width: 800px) {
    flex-direction: row;
    column-gap: 0px;
    align-items: stretch;
    max-width: 90vw;
  }

  img {
    width: 100%;
    height: 280px;
    object-fit: cover;
    border-radius: 12px 12px 0 0;

    @media (max-width: 800px) {
      width: 30%;
      height: auto;
      min-height: 100%;
      border-radius: 12px 0 0 12px;
    }
  }

  h4 {
    p {
      ${TEXT_STYLES[TextVariant.Heading7]};
      margin: 0;

      @media (max-width: 450px) {
        ${TEXT_STYLES[TextVariant.Heading8]};
      }
    }
  }
`;

const GridItemBody = styled.div`
  background: ${Colors.cream};
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  border-radius: 0 0 12px 12px;
  flex: 1;

  @media (max-width: 800px) {
    padding: 24px;
    row-gap: 24px;
    border-radius: 0 12px 12px 0;
  }

  @media (max-width: 450px) {
    padding: 16px;
    row-gap: 16px;
  }

  > p {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: ${Colors.navyBlue};
    margin: 0;
    letter-spacing: -0.1px;

    @media (max-width: 450px) {
      ${TEXT_STYLES[TextVariant.BodyRegularMD]};
    }
  }

  > span {
    letter-spacing: 0;
    ${TEXT_STYLES[TextVariant.BodyBoldMD]};
    color: ${Colors.navyBlue};
    margin: 0;

    @media (max-width: 450px) {
      ${TEXT_STYLES[TextVariant.BodyBoldSM]};
    }
  }
`;

const CTA = styled(LinkButton)`
  margin: 0 auto;
  margin-top: 48px;
  display: inline-flex;

  @media (max-width: 450px) {
    margin-top: 32px;
  }
`;
